import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, formatCurrency } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Loader2, FileDown, Sparkles, Paperclip, RotateCcw, MapPin, Zap, TrendingUp, Gauge, Calculator, Mail, Send, Eye } from 'lucide-react';
import { EmailPreviewDialog } from '@/components/EmailPreviewDialog';
import { PdfPreviewDialog } from '@/components/PdfPreviewDialog';
import { useAuth, isAgent } from '@/contexts/AuthContext';

/**
 * Dialog che consente di rivedere/modificare il business plan e i toggle
 * (includi schede tecniche) prima di scaricare il preventivo PDF.
 *
 * Props:
 *  - open, onOpenChange
 *  - payload: base payload da inviare a /api/preventivo/pdf
 *      { company_id, customer, products, vat_rate, payment, ... }
 *  - onGenerated?: callback invocata dopo il download riuscito
 */
export function QuoteBusinessPlanDialog({ open, onOpenChange, payload, editQuoteId, onGenerated }) {
  const { user: authUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [computed, setComputed] = useState(null); // valori calcolati dal backend
  const [bp, setBp] = useState({}); // override manuali dell'utente
  const [includeDatasheets, setIncludeDatasheets] = useState(true);
  const [includeBP, setIncludeBP] = useState(true); // toggle: includere la sezione Business Plan nel PDF
  const [includePaybackChart, setIncludePaybackChart] = useState(true); // toggle: includere il grafico payback nel PDF
  const [datasheetsCount, setDatasheetsCount] = useState(0);
  const [usePvgis, setUsePvgis] = useState(false);
  const [pvgisInfo, setPvgisInfo] = useState(null);
  const [pvgisLoading, setPvgisLoading] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const handlePdfPreview = async () => {
    setPdfLoading(true);
    setPdfOpen(true);
    setPdfUrl(null);
    try {
      const res = await api.fetchPreventivoPdfUrl(buildGenPayload());
      // res può essere una stringa (URL) o un oggetto { url, warnings }
      const url = (typeof res === 'string') ? res : res?.url;
      const warnings = (typeof res === 'object' && res) ? res.warnings : null;
      setPdfUrl(url);
      if (warnings?.failed_datasheets?.length) {
        const list = warnings.failed_datasheets.slice(0, 3).map(d => d.product || 'Prodotto').join(', ');
        const more = warnings.failed_datasheets.length > 3 ? ` +${warnings.failed_datasheets.length - 3}` : '';
        toast.warning('Alcune schede tecniche non sono state allegate al PDF', {
          description: `${list}${more}. Se sono su Cloudinary, verifica in Log Errori PDF → "Test scheda tecnica Cloudinary".`,
          duration: 9000,
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore generazione anteprima PDF');
      setPdfOpen(false);
    } finally {
      setPdfLoading(false);
    }
  };

  // Fetch computed values when dialog opens
  useEffect(() => {
    if (!open || !payload?.company_id) return;
    setLoading(true);
    setBp({});
    setPvgisInfo(null);
    setUsePvgis(false);
    setEmailTo(payload?.customer?.email || '');
    // Carica i default PDF della company (impostazioni preselezione)
    api.getCompany(payload.company_id)
      .then((company) => {
        if (company) {
          if (company.pdf_default_include_datasheets === false) setIncludeDatasheets(false); else setIncludeDatasheets(true);
          const bpDefault = company.pdf_default_include_bp !== false;
          const pbDefault = company.pdf_default_include_payback !== false;
          setIncludeBP(bpDefault);
          setIncludePaybackChart(bpDefault && pbDefault);
        }
      })
      .catch(() => { /* silente: usa i default hardcoded */ });
    api.previewBusinessPlan({
      company_id: payload.company_id,
      products: payload.products || [],
      vat_rate: payload.vat_rate || 10,
    })
      .then((res) => {
        setComputed(res);
      })
      .catch(() => toast.error('Errore calcolo business plan'))
      .finally(() => setLoading(false));
    // Count product datasheets (both legacy singolo campo e nuovo array)
    const count = (payload.products || []).filter(p => p.datasheet_data_url || p.product_id || (Array.isArray(p.datasheets) && p.datasheets.length > 0)).length;
    setDatasheetsCount(count);
    // eslint-disable-next-line
  }, [open, payload?.company_id, JSON.stringify(payload?.products || [])]);

  // Fetch PVGIS-based estimate when user toggles PVGIS
  const fetchPvgis = async () => {
    const customer = payload?.customer || {};
    // Servono almeno city+province per il geocoding
    if (!customer.residence_city && !customer.residence_province) {
      toast.error('Compila almeno città o provincia del cliente per usare PVGIS');
      return;
    }
    setPvgisLoading(true);
    try {
      const res = await api.previewBusinessPlan({
        company_id: payload.company_id,
        products: payload.products || [],
        vat_rate: payload.vat_rate || 10,
        customer,
        use_pvgis: true,
        location: {
          address: customer.residence_address,
          city: customer.residence_city,
          province: customer.residence_province,
          cap: customer.residence_cap,
        },
      });
      setComputed(res);
      setPvgisInfo(res.pvgis || null);
      if (res.pvgis && res.pvgis.annual_kwh) {
        setUsePvgis(true);
        toast.success(`PVGIS: ${Math.round(res.pvgis.annual_kwh)} kWh/anno per la località selezionata`);
      } else {
        toast.error('Impossibile ottenere la stima PVGIS per questa località');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore chiamata PVGIS');
    } finally {
      setPvgisLoading(false);
    }
  };

  const cleanOverrides = () => Object.fromEntries(
    Object.entries(bp).filter(([, v]) => v !== '' && v !== undefined && v !== null),
  );

  // Ricalcola il business plan lato backend usando i consumi/override correnti
  const recalcBusinessPlan = async () => {
    setRecalcLoading(true);
    try {
      const cust = payload?.customer || {};
      const res = await api.previewBusinessPlan({
        company_id: payload.company_id,
        products: payload.products || [],
        vat_rate: payload.vat_rate || 10,
        business_plan: cleanOverrides(),
        ...(usePvgis ? {
          use_pvgis: true,
          customer: cust,
          location: {
            address: cust.residence_address,
            city: cust.residence_city,
            province: cust.residence_province,
            cap: cust.residence_cap,
          },
        } : {}),
      });
      setComputed(res);
      toast.success('Business plan ricalcolato con i consumi inseriti');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore ricalcolo business plan');
    } finally {
      setRecalcLoading(false);
    }
  };

  const displayValue = (key, formatter = (v) => v) => {
    const override = bp[key];
    if (override !== undefined && override !== '') return formatter(Number(override));
    return formatter(computed?.business_plan?.[key] ?? 0);
  };

  const setOverride = (key, val) => {
    setBp(prev => ({ ...prev, [key]: val }));
  };
  const resetOverride = (key) => {
    setBp(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const buildGenPayload = () => {
    const cust = payload.customer || {};
    return {
      ...payload,
      business_plan: cleanOverrides(),
      include_datasheets: includeDatasheets,
      include_payback_chart: includePaybackChart,
      use_pvgis: usePvgis,
      location: usePvgis ? {
        address: cust.residence_address,
        city: cust.residence_city,
        province: cust.residence_province,
        cap: cust.residence_cap,
      } : undefined,
    };
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const body = { ...buildGenPayload(), ...(includeBP ? {} : { skip_business_plan: true }) };
      const warnings = await api.openPreventivoPdf(body);
      toast.success(includeBP ? 'Preventivo generato e salvato nello storico' : 'Preventivo generato senza Business Plan e salvato nello storico');
      if (warnings?.failed_datasheets?.length) {
        const list = warnings.failed_datasheets.slice(0, 3).map(d => d.product || 'Prodotto').join(', ');
        const more = warnings.failed_datasheets.length > 3 ? ` +${warnings.failed_datasheets.length - 3}` : '';
        toast.warning('Alcune schede tecniche non sono state allegate', {
          description: `${list}${more}. Verifica in Log Errori PDF → "Test scheda tecnica Cloudinary".`,
          duration: 9000,
        });
      }
      onOpenChange?.(false);
      onGenerated?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(detail ? `Errore generazione preventivo: ${String(detail).slice(0, 250)}` : 'Errore generazione preventivo');
      // eslint-disable-next-line no-console
      console.error('Preventivo PDF error:', e?.response?.data || e);
    } finally {
      setGenerating(false);
    }
  };

  const [savingDraft, setSavingDraft] = useState(false);
  const handleSaveDraft = async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    const tId = toast.loading(editQuoteId ? 'Salvataggio modifiche in corso…' : 'Salvataggio preventivo in corso…');
    try {
      const body = { ...buildGenPayload(), ...(includeBP ? {} : { skip_business_plan: true }) };
      let res;
      if (editQuoteId) {
        // Modalità Modifica: aggiorna il preventivo esistente (stesso id/numero)
        res = await api.updateQuote(editQuoteId, {
          products: body.products,
          vat_rate: body.vat_rate,
          payment: body.payment,
          customer_snapshot: body.customer,
          business_plan: body.business_plan,
          notes: body.notes,
          title: body.title,
          validity_days: body.validity_days,
        });
        // Rigenera automaticamente il PDF con i dati aggiornati (skip_autosave così non duplica il record).
        // Il PDF si apre in una nuova scheda: il file archiviato riflette subito le modifiche.
        try {
          toast.loading('Rigenerazione PDF aggiornato…', { id: tId });
          await api.openPreventivoPdf({ ...body, number: res.number || body.number, skip_autosave: true });
          toast.success(`Preventivo ${res.number || ''} aggiornato e PDF rigenerato`, { id: tId });
        } catch (pdfErr) {
          // L'update è comunque riuscito: PDF non generato ma dato salvato
          console.error('Errore rigenerazione PDF:', pdfErr);
          toast.success(`Preventivo ${res.number || ''} aggiornato (PDF non rigenerato: ${pdfErr?.response?.data?.detail || pdfErr.message || 'errore'})`, { id: tId });
        }
      } else {
        // Modalità Nuovo: crea un nuovo preventivo e auto-crea/collega anagrafica
        res = await api.saveQuoteDraft(body);
        toast.success(`Preventivo ${res.number || ''} salvato con successo. Anagrafica ${res.customer_id ? 'collegata' : 'da completare'}.`, { id: tId });
      }
      onOpenChange?.(false);
      onGenerated?.(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio preventivo', { id: tId });
    } finally {
      setSavingDraft(false);
    }
  };

  const handlePreviewEmail = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const res = await api.previewEmail({
        type: 'preventivo',
        company_id: payload.company_id,
        customer: payload.customer || {},
        products: payload.products || [],
        vat_rate: payload.vat_rate || 10,
        number: payload.number || '',
      });
      setPreviewData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento anteprima');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo || !emailTo.includes('@')) {
      toast.error('Inserisci un indirizzo email valido per il cliente');
      return;
    }
    setSendingEmail(true);
    try {
      const res = await api.sendPreventivoEmail({ ...buildGenPayload(), to_email: emailTo });
      toast.success(`Preventivo inviato a ${res.to}`);
    } catch (e) {
      toast.error('Invio email non riuscito', {
        description: e?.response?.data?.detail || 'Il sistema ha riprovato automaticamente senza successo. Riprova tra qualche minuto.',
        duration: 7000,
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const bpVal = computed?.business_plan || {};

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bp-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="size-4 text-primary" />
            Rivedi il Business Plan prima di generare il preventivo
          </DialogTitle>
          <DialogDescription>
            I valori sono precompilati automaticamente in base ai prodotti e ai coefficienti dell&apos;azienda. Modificali manualmente se necessario.
          </DialogDescription>
          <div className="pt-2 flex items-center justify-between gap-3 flex-wrap rounded-lg border bg-secondary/30 px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <Switch
                id="toggle-include-bp"
                checked={includeBP}
                onCheckedChange={setIncludeBP}
                data-testid="bp-include-toggle"
              />
              <div className="min-w-0">
                <label htmlFor="toggle-include-bp" className="text-sm font-medium cursor-pointer select-none">
                  {includeBP ? 'Includi il Business Plan nel PDF' : 'Non includere il Business Plan nel PDF'}
                </label>
                <p className="text-[11px] text-muted-foreground">
                  {includeBP
                    ? 'La sezione BP sarà generata usando i valori qui sotto.'
                    : 'Per prodotti senza risparmio energetico misurabile (wallbox, pompe di calore, climatizzazione). Il PDF salterà tutte le sezioni BP.'}
                </p>
              </div>
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="p-8 flex items-center justify-center text-muted-foreground text-sm gap-2">
            <Loader2 className="size-4 animate-spin" /> Calcolo in corso...
          </div>
        ) : (
          <div className="space-y-5">
            {/* Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <ReadonlyStat label="Potenza FV" value={`${bpVal.kwp || 0} kWp`} />
              <ReadonlyStat label="Prodotti" value={payload?.products?.length || 0} />
              <ReadonlyStat label="Investimento" value={formatCurrency(bpVal.total_investment || 0)} />
              <ReadonlyStat
                label="Break Even"
                value={
                  bpVal.bep_years_int != null
                    ? `${bpVal.bep_years_int}a ${bpVal.bep_leftover_months || 0}m`
                    : `${bpVal.payback_years || 0} anni`
                }
                highlight
              />
            </div>

            {/* PVGIS integration block */}
            {(bpVal.kwp || 0) > 0 && (
              <div className={`rounded-lg border p-3 ${pvgisInfo ? 'bg-primary/5 border-primary/40' : 'bg-secondary/40'}`} data-testid="bp-pvgis-block">
                <div className="flex items-start gap-3">
                  <MapPin className="size-4 text-primary shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">Stima produzione PVGIS</span>
                      {pvgisInfo && <Badge variant="outline" className="border-primary/60 text-primary text-[10px]">Dati JRC European Commission</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {pvgisInfo
                        ? <>Località: <strong>{pvgisInfo.geocoded_address}</strong> · <strong>{Math.round(pvgisInfo.annual_kwh)} kWh/anno</strong> · Resa <strong>{Math.round(pvgisInfo.specific_yield_kwh_per_kwp)} kWh/kWp</strong></>
                        : <>Usa i dati reali di irraggiamento solare della località del cliente per stimare produzione, risparmio e break-even.</>}
                    </p>
                  </div>
                  <Button
                    type="button" size="sm" variant={pvgisInfo ? 'outline' : 'default'}
                    onClick={fetchPvgis} disabled={pvgisLoading}
                    className="shrink-0" data-testid="bp-pvgis-fetch"
                  >
                    {pvgisLoading ? <Loader2 className="size-3.5 mr-1 animate-spin" /> : <Zap className="size-3.5 mr-1" />}
                    {pvgisInfo ? 'Ricalcola' : 'Recupera dati PVGIS'}
                  </Button>
                </div>
                {pvgisInfo && (
                  <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-primary/20">
                    <div className="text-center">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Produzione</div>
                      <div className="text-sm font-semibold tabular-nums">{Math.round(pvgisInfo.annual_kwh)} kWh</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Irraggiamento</div>
                      <div className="text-sm font-semibold tabular-nums">{Math.round(pvgisInfo.h_sun_year)} kWh/m²</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Variabilità</div>
                      <div className="text-sm font-semibold tabular-nums">±{pvgisInfo.variability_pct.toFixed(1)}%</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <Separator />

            {/* Consumi del cliente per BEP reale */}
            <div className="rounded-lg border p-3 bg-secondary/30" data-testid="bp-consumption-block">
              <div className="flex items-start gap-3">
                <Gauge className="size-4 text-primary shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium">Consumi energetici del cliente</span>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Inserisci i consumi annui totali e/o per fasce orarie (da bolletta) per calcolare il <strong>Break Even Point reale</strong>: risparmio da autoconsumo + valorizzazione dell&apos;energia immessa in rete.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                <div className="space-y-1">
                  <Label className="text-xs">Consumo annuo (kWh)</Label>
                  <Input
                    type="number" step="1" min="0" placeholder="es. 3500"
                    value={bp.annual_consumption_kwh ?? ''}
                    onChange={(e) => setOverride('annual_consumption_kwh', e.target.value)}
                    className="tabular-nums h-9"
                    data-testid="bp-consumption-annual"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">F1 (kWh)</Label>
                  <Input
                    type="number" step="1" min="0" placeholder="diurna"
                    value={bp.consumption_f1_kwh ?? ''}
                    onChange={(e) => setOverride('consumption_f1_kwh', e.target.value)}
                    className="tabular-nums h-9"
                    data-testid="bp-consumption-f1"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">F2 (kWh)</Label>
                  <Input
                    type="number" step="1" min="0" placeholder="serale"
                    value={bp.consumption_f2_kwh ?? ''}
                    onChange={(e) => setOverride('consumption_f2_kwh', e.target.value)}
                    className="tabular-nums h-9"
                    data-testid="bp-consumption-f2"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">F3 (kWh)</Label>
                  <Input
                    type="number" step="1" min="0" placeholder="notturna"
                    value={bp.consumption_f3_kwh ?? ''}
                    onChange={(e) => setOverride('consumption_f3_kwh', e.target.value)}
                    className="tabular-nums h-9"
                    data-testid="bp-consumption-f3"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between mt-3">
                <p className="text-[11px] text-muted-foreground">
                  Se compili solo le fasce, il totale annuo viene calcolato automaticamente. La fascia F1 (diurna) aumenta la quota di autoconsumo stimata.
                </p>
                <Button
                  type="button" size="sm" variant="default"
                  onClick={recalcBusinessPlan}
                  disabled={recalcLoading || loading}
                  className="shrink-0 ml-3"
                  data-testid="bp-recalc-button"
                >
                  {recalcLoading ? <Loader2 className="size-3.5 mr-1 animate-spin" /> : <Calculator className="size-3.5 mr-1" />}
                  Ricalcola BEP
                </Button>
              </div>
              {bpVal.annual_consumption_kwh > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 pt-3 border-t border-primary/20" data-testid="bp-consumption-results">
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Autoconsumo</div>
                    <div className="text-sm font-semibold tabular-nums">{Math.round(bpVal.self_consumed_kwh || 0)} kWh</div>
                    <div className="text-[10px] text-muted-foreground">{bpVal.self_consumption_rate_pct || 0}% produzione{bpVal.has_battery ? ' · con accumulo' : ''}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Immessa in rete</div>
                    <div className="text-sm font-semibold tabular-nums">{Math.round(bpVal.grid_feed_kwh || 0)} kWh</div>
                    <div className="text-[10px] text-muted-foreground">a {bpVal.coeff_feed_eur_kwh || 0.1} €/kWh</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Risparmio autoconsumo</div>
                    <div className="text-sm font-semibold tabular-nums">{formatCurrency(bpVal.savings_self_eur || 0)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Ricavo immissione</div>
                    <div className="text-sm font-semibold tabular-nums">{formatCurrency(bpVal.savings_feed_eur || 0)}</div>
                  </div>
                </div>
              )}
            </div>

            <Separator />

            <div className="text-sm font-medium">Valori del business plan <span className="text-xs text-muted-foreground">(modificabili)</span></div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <BpField
                label="Produzione annua (kWh)"
                autoValue={bpVal.annual_kwh}
                bpKey="annual_kwh"
                bp={bp} setOverride={setOverride} resetOverride={resetOverride}
                step="1" testid="bp-annual-kwh"
              />
              <BpField
                label="Risparmio annuo (€)"
                autoValue={bpVal.annual_savings_eur}
                bpKey="annual_savings_eur"
                bp={bp} setOverride={setOverride} resetOverride={resetOverride}
                step="0.01" testid="bp-annual-savings"
              />
              <BpField
                label="Payback (anni)"
                autoValue={bpVal.payback_years}
                bpKey="payback_years"
                bp={bp} setOverride={setOverride} resetOverride={resetOverride}
                step="0.1" testid="bp-payback"
              />
              <BpField
                label="Orizzonte proiezione (anni)"
                autoValue={bpVal.horizon_years}
                bpKey="horizon_years"
                bp={bp} setOverride={setOverride} resetOverride={resetOverride}
                step="1"
              />
              <BpField
                label="CO₂ risparmiata (kg/anno)"
                autoValue={bpVal.co2_saved_kg}
                bpKey="co2_saved_kg"
                bp={bp} setOverride={setOverride} resetOverride={resetOverride}
                step="1" testid="bp-co2"
              />
              <BpField
                label="Alberi equivalenti"
                autoValue={bpVal.trees_equivalent}
                bpKey="trees_equivalent"
                bp={bp} setOverride={setOverride} resetOverride={resetOverride}
                step="1"
              />
              <BpField
                label="Km auto evitati"
                autoValue={bpVal.km_equivalent}
                bpKey="km_equivalent"
                bp={bp} setOverride={setOverride} resetOverride={resetOverride}
                step="1"
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between border rounded-lg p-3">
              <div className="flex items-start gap-3">
                <Paperclip className="size-4 text-primary mt-0.5" />
                <div>
                  <Label htmlFor="include-ds" className="text-sm font-medium cursor-pointer">Allega schede tecniche</Label>
                  <p className="text-xs text-muted-foreground">
                    {datasheetsCount > 0
                      ? `Verranno concatenate automaticamente le schede tecniche associate ai prodotti selezionati.`
                      : `Nessuna scheda tecnica associata ai prodotti in questo preventivo.`}
                  </p>
                </div>
              </div>
              <Switch
                id="include-ds"
                checked={includeDatasheets}
                onCheckedChange={setIncludeDatasheets}
                data-testid="bp-include-datasheets"
              />
            </div>

            {/* Toggle: include payback chart */}
            <div className="border rounded-lg p-3 flex items-start gap-3" data-testid="bp-payback-block">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <Sparkles className="size-4 text-primary mt-0.5" />
                <div className="min-w-0">
                  <label htmlFor="include-payback" className="text-sm font-medium cursor-pointer select-none">
                    {includePaybackChart ? 'Includi il grafico Payback nel PDF' : 'Non includere il grafico Payback nel PDF'}
                  </label>
                  <p className="text-xs text-muted-foreground">
                    {includePaybackChart
                      ? 'La sezione 04 con la proiezione economica (Break Even Point, risparmi cumulati, ROI) verrà inclusa nel PDF.'
                      : 'La sezione di proiezione payback verrà omessa. Le sezioni impatto ambientale e riepilogo importo restano visibili.'}
                    {!includeBP && ' — disabilitato perché hai escluso l\'intero Business Plan sopra.'}
                  </p>
                </div>
              </div>
              <Switch
                id="include-payback"
                checked={includePaybackChart && includeBP}
                disabled={!includeBP}
                onCheckedChange={setIncludePaybackChart}
                data-testid="bp-include-payback-toggle"
              />
            </div>

            {/* Invio via email */}
            {(!isAgent(authUser) || authUser?.agent_permissions?.can_send_email !== false) && (
            <div className="border rounded-lg p-3 space-y-2" data-testid="bp-email-block">
              <div className="flex items-start gap-3">
                <Mail className="size-4 text-primary mt-0.5" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium">Invia il preventivo via email</span>
                  <p className="text-xs text-muted-foreground">Il PDF verrà generato e inviato in allegato all&apos;indirizzo del cliente, con il branding e il testo configurati nel profilo azienda.</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Input
                  type="email"
                  placeholder="email.cliente@esempio.it"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  className="h-9 flex-1 min-w-[200px]"
                  data-testid="bp-email-input"
                />
                <Button
                  type="button" size="sm" variant="ghost"
                  onClick={handlePreviewEmail}
                  disabled={previewLoading}
                  className="h-9 shrink-0"
                  data-testid="bp-email-preview-button"
                >
                  {previewLoading ? <Loader2 className="size-3.5 mr-1 animate-spin" /> : <Eye className="size-3.5 mr-1" />}
                  Anteprima
                </Button>
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={handleSendEmail}
                  disabled={sendingEmail || loading || !emailTo}
                  className="h-9 shrink-0"
                  data-testid="bp-send-email-button"
                >
                  {sendingEmail ? <Loader2 className="size-3.5 mr-1 animate-spin" /> : <Send className="size-3.5 mr-1" />}
                  Invia email
                </Button>
              </div>
            </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={generating || savingDraft}>Annulla</Button>
          <Button variant="outline" onClick={handlePdfPreview} disabled={pdfLoading || loading} data-testid="bp-pdf-preview-button">
            {pdfLoading ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Eye className="size-4 mr-2" />}
            Anteprima PDF
          </Button>
          <Button variant="secondary" onClick={handleSaveDraft} disabled={savingDraft || loading} data-testid="bp-save-draft-button" title={editQuoteId ? 'Salva le modifiche al preventivo esistente' : "Salva il preventivo e crea/collega automaticamente l'anagrafica cliente"}>
            {savingDraft ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Sparkles className="size-4 mr-2" />}
            {editQuoteId ? 'Salva modifiche' : 'Salva preventivo'}
          </Button>
          <Button onClick={handleGenerate} disabled={generating || loading || savingDraft} data-testid="bp-generate-button">
            {generating ? <Loader2 className="size-4 mr-2 animate-spin" /> : <FileDown className="size-4 mr-2" />}
            Genera preventivo PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <EmailPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} preview={previewData} loading={previewLoading} />
    <PdfPreviewDialog open={pdfOpen} onOpenChange={setPdfOpen} title="Anteprima preventivo" pdfUrl={pdfUrl} loading={pdfLoading} filename="preventivo.pdf" />
    </>
  );
}

const ReadonlyStat = ({ label, value, highlight }) => (
  <div className={`rounded-lg border p-2.5 ${highlight ? 'bg-primary/5 border-primary/30' : 'bg-secondary/40'}`}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-sm font-semibold tabular-nums mt-0.5">{value}</div>
  </div>
);

const BpField = ({ label, autoValue, bpKey, bp, setOverride, resetOverride, step, testid }) => {
  const isOverridden = bp[bpKey] !== undefined && bp[bpKey] !== '';
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        {isOverridden && (
          <button
            type="button"
            className="text-[10px] text-primary hover:underline inline-flex items-center gap-1"
            onClick={() => resetOverride(bpKey)}
          >
            <RotateCcw className="size-3" />
            Auto
          </button>
        )}
        {!isOverridden && (
          <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4">Auto</Badge>
        )}
      </div>
      <Input
        type="number"
        step={step}
        value={isOverridden ? bp[bpKey] : (autoValue ?? '')}
        onChange={(e) => setOverride(bpKey, e.target.value)}
        className={`tabular-nums h-9 ${isOverridden ? 'border-primary' : ''}`}
        data-testid={testid}
      />
    </div>
  );
};
